import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Send, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';
import { hotels } from '../data';

const RequestQuotePage = () => {
  const { language, getLocalizedPath } = useLanguage();
  const isEn = language === 'en';
  const [searchParams] = useSearchParams();

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // States til destination og hotel (kan præudfyldes fra URL)
  const [selectedDestination, setSelectedDestination] = useState('');
  const [hotelName, setHotelName] = useState('');

  useEffect(() => {
    const hotelParam = searchParams.get('hotel');
    const destParam = searchParams.get('destination');
    
    if (destParam) {
      setSelectedDestination(destParam.toLowerCase());
    } else if (hotelParam) {
      const matched = hotels.find((h) => h.name.toLowerCase() === hotelParam.toLowerCase());
      if (matched) {
        setSelectedDestination(matched.destinationId);
      }
    }

    if (hotelParam) {
      setHotelName(hotelParam);
    }
  }, [searchParams]);

  // Filtrerede hoteller baseret på valgte destination
  const filteredHotels = selectedDestination
    ? (selectedDestination !== 'andet'
        ? hotels.filter((h) => h.destinationId === selectedDestination)
        : hotels)
    : [];

  const handleDestinationChange = (e) => {
    const newDest = e.target.value;
    setSelectedDestination(newDest);

    // Hvis den nuværende valgte hotel ikke tilhører den nye destination, nulstilles hotel-feltet
    if (hotelName && newDest && newDest !== 'andet') {
      const currentHotelObj = hotels.find((h) => h.name.toLowerCase() === hotelName.toLowerCase());
      if (currentHotelObj && currentHotelObj.destinationId !== newDest) {
        setHotelName('');
      }
    }
  };

  const handleHotelChange = (e) => {
    const val = e.target.value;
    setHotelName(val);

    // Hvis kunden vælger et kendt hotel fra datalisten, sætter vi automatisk den matchende destination
    const matchedHotel = hotels.find((h) => h.name.toLowerCase() === val.toLowerCase());
    if (matchedHotel) {
      setSelectedDestination(matchedHotel.destinationId);
    }
  };

  // States for betinget visning af felter / Conditional field visibility
  const [childrenCount, setChildrenCount] = useState(0);
  const [hasManualChair, setHasManualChair] = useState(false);
  const [hasElectricChair, setHasElectricChair] = useState(false);
  const [electricBatteryType, setElectricBatteryType] = useState('dry');
  
  const [hasRoomDouble, setHasRoomDouble] = useState(false);
  const [hasRoomSingle, setHasRoomSingle] = useState(false);
  const [hasRoomHandicap, setHasRoomHandicap] = useState(false);

  const [needAids, setNeedAids] = useState(false);
  const [needExtraCare, setNeedExtraCare] = useState(false);

  const texts = {
    seoTitle: isEn ? "Request Quote for Accessible Travel" : "Indhent tilbud på handicapvenlig rejse",
    seoDesc: isEn 
      ? "Fill out our form and get a tailored, non-binding quote for an accessible holiday adapted to your specific needs, aids, and wishes."
      : "Udfyld vores formular og få et skræddersyet, uforpligtende tilbud på en handicapvenlig rejse tilpasset dine specifikke behov, hjælpemidler og ønsker.",
    
    successTitle: isEn ? "Thank you for your inquiry!" : "Tak for din forespørgsel!",
    successDesc: isEn 
      ? "We have received your information and will review it as soon as possible. We typically get back to you with a non-binding quote within 1-2 business days."
      : "Vi har modtaget dine oplysninger og vil gennemgå dem hurtigst muligt. Vi vender tilbage med et uforpligtende tilbud typisk indenfor 1-2 hverdage.",
    successBtn: isEn ? "Send a new inquiry" : "Send en ny forespørgsel",

    title: isEn ? "Request a Quote" : "Indhent Tilbud",
    subtitle: isEn 
      ? "The 'from' price indicates the price per person for 2 travelers and depends on the season, hotel, flights, and specific wishes."
      : "Fra-prisen indikerer prisen pr. person ved 2 rejsende og afhænger af sæson, hotel, fly og specifikke ønsker.",

    personalInfo: isEn ? "Personal Information" : "Personlige Oplysninger",
    name: isEn ? "Name *" : "Navn *",
    email: isEn ? "Email *" : "E-mail *",
    phone: isEn ? "Phone *" : "Telefon *",

    travelDetails: isEn ? "Travel Details" : "Rejsedetaljer",
    destination: isEn ? "Desired destination" : "Ønsket rejsemål",
    destMallorca: isEn ? "Mallorca" : "Mallorca",
    destCrete: isEn ? "Crete" : "Kreta",
    destVenice: isEn ? "Venice" : "Venedig",
    destAustria: isEn ? "Austria" : "Østrig",
    destParis: isEn ? "Paris" : "Paris",
    destOther: isEn ? "Other / Don't know yet" : "Andet / Ved ikke endnu",

    hotel: isEn ? "Desired hotel" : "Ønsket hotel",
    hotelPlaceholder: isEn 
      ? "e.g. Zoëtry Mallorca, Eria Resort, or write if you have a specific request" 
      : "f.eks. Zoëtry Mallorca, Eria Resort, eller skriv hvis du har et specifikt ønske",

    departure: isEn ? "Expected departure" : "Forventet afrejse",
    return: isEn ? "Expected return" : "Forventet hjemrejse",
    flexibility: isEn ? "Flexibility" : "Fleksibilitet",
    flexExact: isEn ? "Exact date" : "Præcis dato",
    flex3: isEn ? "+/- 3 days" : "+/- 3 dage",
    flex5: isEn ? "+/- 5 days" : "+/- 5 dage",
    flex7: isEn ? "+/- 7 days" : "+/- 7 dage",
    flex14: isEn ? "+/- 14 days" : "+/- 14 dage",

    departureAirport: isEn ? "Departure airport" : "Afrejselufthavn",
    selectAirport: isEn ? "Select airport" : "Vælg lufthavn",
    airportBillund: isEn ? "Billund" : "Billund",
    airportKastrup: isEn ? "Copenhagen (Kastrup)" : "Kastrup",
    airportAarhus: isEn ? "Aarhus" : "Århus",
    airportAalborg: isEn ? "Aalborg" : "Aalborg",
    airportHamburg: isEn ? "Hamburg" : "Hamborg",

    duration: isEn ? "Duration of travel" : "Rejsens varighed",
    duration1w: isEn ? "1 week" : "1 uge",
    duration2w: isEn ? "2 weeks" : "2 uger",
    durationCustom: isEn ? "Custom duration" : "Valgfri længde",

    adultsCount: isEn ? "Total number of adults" : "Samlet antal voksne",
    childrenCount: isEn ? "Total number of children" : "Samlet antal børn",
    childrenAges: isEn ? "Children's ages" : "Børns alder",
    childrenAgesPlaceholder: isEn ? "e.g. 5, 8" : "f.eks. 5, 8",

    needsAids: isEn ? "Needs & Aids" : "Behov & Hjælpemidler",
    wheelchairType: isEn ? "Type of wheelchair:" : "Type kørestol:",
    manualChair: isEn ? "Manual wheelchair" : "Manuel stol",
    electricChair: isEn ? "Electric wheelchair" : "El-stol",
    batteryType: isEn ? "Battery type:" : "Batteritype:",
    dryBattery: isEn ? "Dry battery" : "Tørt batteri",
    wetBattery: isEn ? "Wet battery" : "Vådt batteri",

    transportExtra: isEn ? "Transport & Extras:" : "Transport & Ekstra:",
    needLiftbus: isEn ? "Need lift bus transfer" : "Behov for liftbus",
    aids: isEn ? "Aids / Equipment" : "Hjælpemidler",
    aidsQuestion: isEn ? "Which aids are you bringing or wish to rent?" : "Hvilke hjælpemidler medbringes eller ønskes lejet?",
    aidsPlaceholder: isEn ? "e.g. shower chair, hoist, etc." : "f.eks. badestol, lift, etc.",
    extraCare: isEn ? "Need for extra care/assistance" : "Behov for ekstra pleje/hjælp",
    extraCareQuestion: isEn ? "Describe need for extra care/assistance:" : "Beskriv behov for ekstra pleje/hjælp:",
    extraCarePlaceholder: isEn ? "e.g. help with personal care, dressing, hoist use..." : "f.eks. hjælp til personlig pleje, påklædning, liftbrug...",

    roomTypeAndQty: isEn ? "Room type and quantity:" : "Værelsestype og antal:",
    roomHandicap: isEn ? "Accessible room" : "Handicapvenligt værelse",
    roomCount: isEn ? "Number of rooms:" : "Antal værelser:",
    roomHandicapAdults: isEn ? "Number of adults in accessible room *" : "Antal voksne i handicapværelse *",
    roomHandicapChildren: isEn ? "Children in accessible room" : "Børn i handicapværelse",
    roomHandicapChildrenAges: isEn ? "Ages of children" : "Alder på børn",

    roomDouble: isEn ? "Standard double room" : "Dobbeltværelse standard",
    roomStandardAdults: isEn ? "Number of adults in standard room *" : "Antal voksne i standardværelse *",
    roomStandardChildren: isEn ? "Children in standard room" : "Børn i standardværelse",
    roomStandardChildrenAges: isEn ? "Ages of children" : "Alder på børn",
    roomStandardChildrenAgesPlaceholder: isEn ? "e.g. 4, 11" : "f.eks. 4, 11",

    roomSingle: isEn ? "Single room" : "Enkeltværelse (Single)",

    chairDimensions: isEn ? "Wheelchair dimensions and weight" : "Mål og vægt på kørestol",
    chairDimensionsDesc: isEn 
      ? "Please specify the dimensions and weight of the wheelchair(s) being brought on the trip."
      : "Angiv venligst målene og vægten på de(n) kørestol(e), der medbringes på rejsen.",
    
    height: isEn ? "Height (cm)" : "Højde (cm)",
    width: isEn ? "Width (cm)" : "Bredde (cm)",
    depth: isEn ? "Depth (cm)" : "Dybde (cm)",
    weight: isEn ? "Weight (kg)" : "Vægt (kg)",

    specialNeeds: isEn ? "Special needs" : "Særlige behov",
    specialNeedsPlaceholder: isEn ? "Describe any special needs here..." : "Beskriv eventuelle særlige behov her...",
    importantInfo: isEn ? "Important information:" : "Vigtig information:",
    importantInfoDesc: isEn
      ? "Please write as detailed as possible about your special needs, including how self-reliant you are in daily life and during the trip. It is important that we receive this information in writing, as it is the customer's responsibility to inform us correctly about needs for help, assistance, and accessibility."
      : "Skriv gerne så detaljeret som muligt om dine særlige behov, herunder hvor selvhjulpen du er i hverdagen og på rejsen. Det er vigtigt, at vi modtager disse oplysninger skriftligt, da det er kundens ansvar at informere os korrekt om behov for hjælp, assistance og tilgængelighed.",
    
    additionalComments: isEn ? "Additional comments or special requests" : "Yderligere kommentarer eller specielle ønsker",
    
    disclaimer: isEn
      ? "* The customer is obliged to provide Alora Rejser with accurate information and dimensions of the wheelchair, as well as information about the need for aids and how self-reliant they are."
      : "* Kunden er forpligtet til at videregive Alora Rejser reel info og mål på kørestol, samt info om behov for hjælpemidler og hvor selvhjulpen man er.",
    
    consentText: isEn
      ? "I consent to Alora Rejser processing my personal data in order to respond to my request."
      : "Jeg giver samtykke til, at Alora Rejser må behandle mine personoplysninger med henblik på at besvare min henvendelse.",

    submitBtn: isEn ? "Request Quote" : "Indhent tilbud",
    sendingBtn: isEn ? "Sending..." : "Sender..."
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Mappings til letforståeligt dansk for administratoren/kunden
    const destinationNames = {
      mallorca: 'Mallorca',
      kreta: 'Kreta',
      venedig: 'Venedig',
      oestrig: 'Østrig',
      paris: 'Paris',
      andet: 'Andet'
    };

    const flexibilityNames = {
      exact: 'Præcis på disse datoer',
      '3_days': '+/- 3 dage',
      '5_days': '+/- 5 dage',
      '7_days': '+/- 7 dage',
      '14_days': '+/- 14 dage'
    };

    const airportNames = {
      billund: 'Billund',
      kastrup: 'København (Kastrup)',
      aarhus: 'Aarhus',
      aalborg: 'Aalborg',
      hamborg: 'Hamborg'
    };

    const durationNames = {
      '1_week': '1 uge',
      '2_weeks': '2 uger',
      custom: 'Andet / Fleksibel'
    };

    const danishData = {
      "Formular": "Indhent tilbud",
      "Navn": data.name || '',
      "E-mail": data.email || '',
      "Telefon": data.phone || '',
    };

    // Rejsemål og hotel
    if (data.destination) {
      danishData["Rejsemål"] = destinationNames[data.destination] || data.destination;
    }
    if (data.hotel && data.hotel.trim()) {
      danishData["Ønsket hotel"] = data.hotel.trim();
    }

    // Datoer og lufthavn
    if (data.travel_date) {
      danishData["Afrejsedato"] = data.travel_date;
    }
    if (data.return_date) {
      danishData["Hjemrejsedato"] = data.return_date;
    }
    if (data.travel_date_flexibility) {
      danishData["Fleksibilitet på datoer"] = flexibilityNames[data.travel_date_flexibility] || data.travel_date_flexibility;
    }
    if (data.departure_airport) {
      danishData["Afrejselufthavn"] = airportNames[data.departure_airport] || data.departure_airport;
    }
    if (data.duration) {
      danishData["Rejsens varighed"] = durationNames[data.duration] || data.duration;
    }

    // Antal rejsende
    danishData["Antal voksne"] = data.adults_count || '1';
    if (parseInt(data.children_count, 10) > 0) {
      danishData["Antal børn"] = data.children_count;
      if (data.children_ages && data.children_ages.trim()) {
        danishData["Børnenes aldre"] = data.children_ages.trim();
      }
    } else {
      danishData["Antal børn"] = "0";
    }

    // Kørestol og hjælpemidler
    if (hasManualChair) {
      danishData["Manuel kørestol"] = "Ja";
      const manualSpecs = [];
      if (data.chair_manual_height) manualSpecs.push(`Højde: ${data.chair_manual_height} cm`);
      if (data.chair_manual_width) manualSpecs.push(`Bredde: ${data.chair_manual_width} cm`);
      if (data.chair_manual_depth) manualSpecs.push(`Dybde: ${data.chair_manual_depth} cm`);
      if (data.chair_manual_weight) manualSpecs.push(`Vægt: ${data.chair_manual_weight} kg`);
      if (manualSpecs.length > 0) {
        danishData["Manuel kørestol mål"] = manualSpecs.join(', ');
      }
    }

    if (hasElectricChair) {
      const batt = electricBatteryType === 'dry' 
        ? 'Tørbatteri (Gel / AGM / Lithium)' 
        : (electricBatteryType === 'wet' ? 'Vådbatteri' : 'Ikke oplyst');
      danishData["El-kørestol"] = `Ja (Batteritype: ${batt})`;
      const electricSpecs = [];
      if (data.chair_electric_height) electricSpecs.push(`Højde: ${data.chair_electric_height} cm`);
      if (data.chair_electric_width) electricSpecs.push(`Bredde: ${data.chair_electric_width} cm`);
      if (data.chair_electric_depth) electricSpecs.push(`Dybde: ${data.chair_electric_depth} cm`);
      if (data.chair_electric_weight) electricSpecs.push(`Vægt: ${data.chair_electric_weight} kg`);
      if (electricSpecs.length > 0) {
        danishData["El-kørestol mål"] = electricSpecs.join(', ');
      }
    }

    if (!hasManualChair && !hasElectricChair) {
      danishData["Kørestol"] = "Nej";
    }

    danishData["Behov for liftbus"] = data.need_liftbus === 'on' ? "Ja" : "Nej";

    if (data.need_aids === 'on') {
      danishData["Behov for hjælpemidler"] = data.aids_details && data.aids_details.trim() 
        ? `Ja (${data.aids_details.trim()})` 
        : "Ja";
    } else {
      danishData["Behov for hjælpemidler"] = "Nej";
    }

    if (data.need_extra_care === 'on') {
      danishData["Behov for ekstra pleje"] = data.extra_care_details && data.extra_care_details.trim() 
        ? `Ja (${data.extra_care_details.trim()})` 
        : "Ja";
    } else {
      danishData["Behov for ekstra pleje"] = "Nej";
    }

    // Værelsestyper
    if (hasRoomHandicap) {
      let handicapDesc = `${data.room_handicap_count || 1} stk.`;
      const guests = [];
      if (data.room_handicap_adults) guests.push(`${data.room_handicap_adults} voksne`);
      if (data.room_handicap_children && parseInt(data.room_handicap_children, 10) > 0) {
        guests.push(`${data.room_handicap_children} børn${data.room_handicap_children_ages ? ` (alder: ${data.room_handicap_children_ages})` : ''}`);
      }
      if (guests.length > 0) handicapDesc += ` (${guests.join(', ')})`;
      danishData["Handicapvenligt værelse"] = handicapDesc;
    }

    if (hasRoomDouble) {
      let doubleDesc = `${data.room_double_count || 1} stk.`;
      const guests = [];
      if (data.room_standard_adults) guests.push(`${data.room_standard_adults} voksne`);
      if (data.room_standard_children && parseInt(data.room_standard_children, 10) > 0) {
        guests.push(`${data.room_standard_children} børn${data.room_standard_children_ages ? ` (alder: ${data.room_standard_children_ages})` : ''}`);
      }
      if (guests.length > 0) doubleDesc += ` (${guests.join(', ')})`;
      danishData["Standard dobbeltværelse"] = doubleDesc;
    }

    if (hasRoomSingle) {
      danishData["Enkeltværelse"] = `${data.room_single_count || 1} stk.`;
    }

    if (!hasRoomHandicap && !hasRoomDouble && !hasRoomSingle) {
      danishData["Værelsestype"] = "Ikke specificeret";
    }

    // Særlige behov & kommentarer
    if (data.special_needs && data.special_needs.trim()) {
      danishData["Særlige behov og selvhjulpenhed"] = data.special_needs.trim();
    }

    if (data.comments && data.comments.trim()) {
      danishData["Yderligere kommentarer og ønsker"] = data.comments.trim();
    }

    danishData["Samtykke til persondata"] = data.consent === 'on' ? "Givet" : "Ikke givet";
    danishData["Indsendt fra sprog"] = isEn ? "Engelsk side (/en/request-quote)" : "Dansk side (/da/tilbud)";

    // Honeypot bevares altid uændret til antispam
    danishData["website_url"] = data.website_url || "";

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(danishData),
      });

      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-32 pb-20 px-4 min-h-screen flex items-center justify-center bg-stone-50 relative">
        {/* Weak background image overlay */}
        <div 
          className="absolute inset-0 z-0 opacity-15 pointer-events-none fixed"
          style={{
            backgroundImage: 'url("https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg")',
            backgroundPosition: 'center',
            backgroundSize: 'cover'
          }}
        ></div>
        <div className="max-w-md w-full bg-white p-12 rounded-3xl shadow-xl text-center relative z-10">
          <div className="w-20 h-20 bg-[#99A18C]/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="w-10 h-10 text-[#99A18C]" />
          </div>
          <h2 className="text-3xl font-serif text-[#99A18C] mb-4">{texts.successTitle}</h2>
          <p className="text-gray-600 mb-8">
            {texts.successDesc}
          </p>
          <button 
            onClick={() => setSubmitted(false)}
            className="w-full bg-[#99A18C] text-white py-4 rounded-xl font-bold hover:bg-[#8a917e] transition-colors"
          >
            {texts.successBtn}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-20 px-4 min-h-screen bg-stone-50 relative">
      <SEO 
        title={texts.seoTitle} 
        description={texts.seoDesc}
      />
      {/* Weak background image overlay */}
      <div 
        className="absolute inset-0 z-0 opacity-15 pointer-events-none fixed"
        style={{
          backgroundImage: 'url("https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg")',
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      ></div>
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-serif text-[#99A18C] mb-6">{texts.title}</h1>
          <p className="text-sm md:text-base text-gray-500 max-w-xl mx-auto mb-6">
            {texts.subtitle}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-[#99A18C]/10">
          {/* Honeypot field */}
          <div className="sr-only absolute -top-9999 left-0">
            <input type="text" name="website_url" tabIndex="-1" autoComplete="off" />
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Personlige oplysninger */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-[#99A18C] border-b border-[#99A18C]/10 pb-2">{texts.personalInfo}</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{texts.name}</label>
                <input required type="text" name="name" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{texts.email}</label>
                <input required type="email" name="email" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{texts.phone}</label>
                <input required type="tel" name="phone" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all" />
              </div>
            </div>

            {/* Rejsedetaljer */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-[#99A18C] border-b border-[#99A18C]/10 pb-2">{texts.travelDetails}</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{texts.destination}</label>
                <select 
                  name="destination" 
                  value={selectedDestination}
                  onChange={handleDestinationChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all"
                >
                  <option value="">{isEn ? "Select destination" : "Vælg rejsemål"}</option>
                  <option value="mallorca">{texts.destMallorca}</option>
                  <option value="kreta">{texts.destCrete}</option>
                  <option value="venedig">{texts.destVenice}</option>
                  <option value="oestrig">{texts.destAustria}</option>
                  <option value="paris">{texts.destParis}</option>
                  <option value="andet">{texts.destOther}</option>
                </select>
              </div>
              {Boolean(selectedDestination) && filteredHotels.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{texts.hotel}</label>
                  <input 
                    type="text" 
                    name="hotel" 
                    list="hotel-suggestions"
                    value={hotelName}
                    onChange={handleHotelChange}
                    placeholder={texts.hotelPlaceholder} 
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all" 
                  />
                  <datalist id="hotel-suggestions">
                    {filteredHotels.map((h) => (
                      <option key={h.id} value={h.name} />
                    ))}
                  </datalist>
                  {selectedDestination !== 'andet' ? (
                    <p className="text-xs text-stone-500 mt-1.5 flex items-center gap-1.5">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#99A18C]"></span>
                      {isEn 
                        ? `Showing ${filteredHotels.length} ${filteredHotels.length === 1 ? 'hotel' : 'hotels'} available for selected destination`
                        : `Viser ${filteredHotels.length} ${filteredHotels.length === 1 ? 'hotel' : 'hoteller'} tilgængelige for det valgte rejsemål`
                      }
                    </p>
                  ) : (
                    <p className="text-xs text-stone-500 mt-1.5 flex items-center gap-1.5">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#99A18C]/50"></span>
                      {isEn 
                        ? `Showing all ${hotels.length} hotels in suggestions`
                        : `Viser alle ${hotels.length} hoteller i forslagslisten`
                      }
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{texts.departure}</label>
                  <input type="date" name="travel_date" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{texts.return}</label>
                  <input type="date" name="return_date" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{texts.flexibility}</label>
                  <select name="travel_date_flexibility" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all">
                    <option value="exact">{texts.flexExact}</option>
                    <option value="3_days">{texts.flex3}</option>
                    <option value="5_days">{texts.flex5}</option>
                    <option value="7_days">{texts.flex7}</option>
                    <option value="14_days">{texts.flex14}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{texts.departureAirport}</label>
                  <select name="departure_airport" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all">
                    <option value="">{texts.selectAirport}</option>
                    <option value="billund">{texts.airportBillund}</option>
                    <option value="kastrup">{texts.airportKastrup}</option>
                    <option value="aarhus">{texts.airportAarhus}</option>
                    <option value="aalborg">{texts.airportAalborg}</option>
                    <option value="hamborg">{texts.airportHamburg}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{texts.duration}</label>
                <div className="grid grid-cols-3 gap-3">
                  <label className="flex items-center justify-center space-x-2 text-gray-700 cursor-pointer group bg-stone-50 hover:bg-stone-100/80 px-3 py-3 rounded-xl border border-stone-200/60 transition-all">
                    <input 
                      type="radio" 
                      name="duration" 
                      value="1_week" 
                      defaultChecked
                      className="w-4 h-4 text-[#99A18C] focus:ring-[#99A18C]/20" 
                    />
                    <span className="group-hover:text-[#99A18C] transition-colors text-xs sm:text-sm font-medium">{texts.duration1w}</span>
                  </label>
                  <label className="flex items-center justify-center space-x-2 text-gray-700 cursor-pointer group bg-stone-50 hover:bg-stone-100/80 px-3 py-3 rounded-xl border border-stone-200/60 transition-all">
                    <input 
                      type="radio" 
                      name="duration" 
                      value="2_weeks" 
                      className="w-4 h-4 text-[#99A18C] focus:ring-[#99A18C]/20" 
                    />
                    <span className="group-hover:text-[#99A18C] transition-colors text-xs sm:text-sm font-medium">{texts.duration2w}</span>
                  </label>
                  <label className="flex items-center justify-center space-x-2 text-gray-700 cursor-pointer group bg-stone-50 hover:bg-stone-100/80 px-3 py-3 rounded-xl border border-stone-200/60 transition-all">
                    <input 
                      type="radio" 
                      name="duration" 
                      value="custom" 
                      className="w-4 h-4 text-[#99A18C] focus:ring-[#99A18C]/20" 
                    />
                    <span className="group-hover:text-[#99A18C] transition-colors text-xs sm:text-sm font-medium">{texts.durationCustom}</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{texts.adultsCount}</label>
                <input type="number" name="adults_count" min="1" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all" />
              </div>
              <div className={`grid gap-4 ${childrenCount > 0 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{texts.childrenCount}</label>
                  <input 
                    type="number" 
                    name="children_count" 
                    min="0" 
                    value={childrenCount}
                    onChange={(e) => setChildrenCount(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all" 
                  />
                </div>
                {childrenCount > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{texts.childrenAges}</label>
                    <input type="text" name="children_ages" placeholder={texts.childrenAgesPlaceholder} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Behov & Hjælpemidler */}
          <div className="space-y-8 mb-12">
            <h3 className="text-xl font-bold text-[#99A18C] border-b border-[#99A18C]/10 pb-2">{texts.needsAids}</h3>
            
            {/* Skjulte felter til bagudkompatibilitet med modtager-systemet */}
            <input type="hidden" name="chair_electric_dry" value={hasElectricChair && electricBatteryType === 'dry' ? 'on' : ''} />
            <input type="hidden" name="chair_electric_wet" value={hasElectricChair && electricBatteryType === 'wet' ? 'on' : ''} />

            <div className="grid md:grid-cols-2 gap-8">
              {/* Venstre kolonne: Kørestol & Transport */}
              <div className="space-y-6">
                {/* Type kørestol */}
                <div className="space-y-4">
                  <p className="font-medium text-gray-800">{texts.wheelchairType}</p>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3 text-gray-700 cursor-pointer group">
                      <input 
                        type="checkbox" 
                        name="chair_manual" 
                        checked={hasManualChair}
                        onChange={(e) => setHasManualChair(e.target.checked)}
                        className="w-5 h-5 text-[#99A18C] rounded focus:ring-[#99A18C]/20" 
                      />
                      <span className="group-hover:text-[#99A18C] transition-colors">{texts.manualChair}</span>
                    </label>
                    
                    <div className="space-y-2">
                      <label className="flex items-center space-x-3 text-gray-700 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          name="chair_electric" 
                          checked={hasElectricChair}
                          onChange={(e) => setHasElectricChair(e.target.checked)}
                          className="w-5 h-5 text-[#99A18C] rounded focus:ring-[#99A18C]/20" 
                        />
                        <span className="group-hover:text-[#99A18C] transition-colors">{texts.electricChair}</span>
                      </label>
                      
                      {hasElectricChair && (
                        <div className="pl-8 space-y-2 border-l-2 border-[#99A18C]/20 py-1 transition-all duration-300">
                          <p className="text-xs font-medium text-gray-500">{texts.batteryType}</p>
                          <div className="flex gap-4">
                            <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer group">
                              <input 
                                type="radio" 
                                name="chair_electric_battery" 
                                value="dry"
                                checked={electricBatteryType === 'dry'}
                                onChange={() => setElectricBatteryType('dry')}
                                className="w-4 h-4 text-[#99A18C] focus:ring-[#99A18C]/20" 
                              />
                              <span className="group-hover:text-[#99A18C] transition-colors">{texts.dryBattery}</span>
                            </label>
                            <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer group">
                              <input 
                                type="radio" 
                                name="chair_electric_battery" 
                                value="wet"
                                checked={electricBatteryType === 'wet'}
                                onChange={() => setElectricBatteryType('wet')}
                                className="w-4 h-4 text-[#99A18C] focus:ring-[#99A18C]/20" 
                              />
                              <span className="group-hover:text-[#99A18C] transition-colors">{texts.wetBattery}</span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Transport & Ekstra */}
                <div className="space-y-4 pt-2 border-t border-stone-100">
                  <p className="font-medium text-gray-800">{texts.transportExtra}</p>
                  <div className="space-y-4">
                    <label className="flex items-center space-x-3 text-gray-700 cursor-pointer group">
                      <input type="checkbox" name="need_liftbus" className="w-5 h-5 text-[#99A18C] rounded focus:ring-[#99A18C]/20" />
                      <span className="group-hover:text-[#99A18C] transition-colors">{texts.needLiftbus}</span>
                    </label>
                    
                    <div className="space-y-2">
                      <label className="flex items-center space-x-3 text-gray-700 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          name="need_aids" 
                          checked={needAids}
                          onChange={(e) => setNeedAids(e.target.checked)}
                          className="w-5 h-5 text-[#99A18C] rounded focus:ring-[#99A18C]/20" 
                        />
                        <span className="group-hover:text-[#99A18C] transition-colors">{texts.aids}</span>
                      </label>
                      {needAids && (
                        <div className="pl-8 pt-1 border-l-2 border-[#99A18C]/20 space-y-2 transition-all duration-300">
                          <label className="block text-xs font-medium text-gray-600">{texts.aidsQuestion}</label>
                          <input 
                            type="text" 
                            name="aids_details" 
                            placeholder={texts.aidsPlaceholder} 
                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" 
                          />
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center space-x-3 text-gray-700 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          name="need_extra_care" 
                          checked={needExtraCare}
                          onChange={(e) => setNeedExtraCare(e.target.checked)}
                          className="w-5 h-5 text-[#99A18C] rounded focus:ring-[#99A18C]/20" 
                        />
                        <span className="group-hover:text-[#99A18C] transition-colors">{texts.extraCare}</span>
                      </label>
                      {needExtraCare && (
                        <div className="pl-8 pt-1 border-l-2 border-[#99A18C]/20 space-y-2 transition-all duration-300">
                          <label className="block text-xs font-medium text-gray-600">{texts.extraCareQuestion}</label>
                          <textarea 
                            name="extra_care_details" 
                            rows="3" 
                            placeholder={texts.extraCarePlaceholder} 
                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all"
                          ></textarea>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Højre kolonne: Værelsestype og antal */}
              <div className="space-y-4">
                <p className="font-medium text-gray-800 mb-3">{texts.roomTypeAndQty}</p>
                <div className="space-y-4">
                  {/* Handicapvenligt værelse */}
                  <div className="bg-stone-50/50 p-4 rounded-xl border border-stone-100 space-y-4 transition-all">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center space-x-3 text-gray-700 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          name="room_handicap" 
                          checked={hasRoomHandicap}
                          onChange={(e) => setHasRoomHandicap(e.target.checked)}
                          className="w-5 h-5 text-[#99A18C] rounded focus:ring-[#99A18C]/20" 
                        />
                        <span className="group-hover:text-[#99A18C] transition-colors font-semibold text-stone-800">{texts.roomHandicap}</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{texts.roomCount}</span>
                        <input 
                          type="number" 
                          name="room_handicap_count" 
                          min="0" 
                          placeholder="0"
                          className="w-16 px-2 py-1 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white text-center font-semibold" 
                        />
                      </div>
                    </div>
                    {hasRoomHandicap && (
                      <div className="pl-8 pt-3 border-t border-stone-200/60 space-y-3 transition-all duration-300">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{texts.roomHandicapAdults}</label>
                          <input 
                            type="number" 
                            name="room_handicap_adults" 
                            min="1" 
                            placeholder="f.eks. 2"
                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" 
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{texts.roomHandicapChildren}</label>
                            <input 
                              type="number" 
                              name="room_handicap_children" 
                              min="0" 
                              placeholder="0"
                              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" 
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{texts.roomHandicapChildrenAges}</label>
                            <input 
                              type="text" 
                              name="room_handicap_children_ages" 
                              placeholder={texts.childrenAgesPlaceholder}
                              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" 
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Dobbeltværelse standard */}
                  <div className="bg-stone-50/50 p-4 rounded-xl border border-stone-100 space-y-4 transition-all">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center space-x-3 text-gray-700 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          name="room_double" 
                          checked={hasRoomDouble}
                          onChange={(e) => setHasRoomDouble(e.target.checked)}
                          className="w-5 h-5 text-[#99A18C] rounded focus:ring-[#99A18C]/20" 
                        />
                        <span className="group-hover:text-[#99A18C] transition-colors font-semibold text-stone-800">{texts.roomDouble}</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{texts.roomCount}</span>
                        <input 
                          type="number" 
                          name="room_double_count" 
                          min="0" 
                          placeholder="0"
                          className="w-16 px-2 py-1 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white text-center font-semibold" 
                        />
                      </div>
                    </div>
                    {hasRoomDouble && (
                      <div className="pl-8 pt-3 border-t border-stone-200/60 space-y-3 transition-all duration-300">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{texts.roomStandardAdults}</label>
                          <input 
                            type="number" 
                            name="room_standard_adults" 
                            min="1" 
                            placeholder="f.eks. 2"
                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" 
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{texts.roomStandardChildren}</label>
                            <input 
                              type="number" 
                              name="room_standard_children" 
                              min="0" 
                              placeholder="0"
                              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" 
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{texts.roomStandardChildrenAges}</label>
                            <input 
                              type="text" 
                              name="room_standard_children_ages" 
                              placeholder={texts.roomStandardChildrenAgesPlaceholder}
                              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" 
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Enkeltværelse */}
                  <div className="bg-stone-50/50 p-4 rounded-xl border border-stone-100 space-y-4 transition-all">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center space-x-3 text-gray-700 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          name="room_single" 
                          checked={hasRoomSingle}
                          onChange={(e) => setHasRoomSingle(e.target.checked)}
                          className="w-5 h-5 text-[#99A18C] rounded focus:ring-[#99A18C]/20" 
                        />
                        <span className="group-hover:text-[#99A18C] transition-colors font-semibold text-stone-800">{texts.roomSingle}</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{texts.roomCount}</span>
                        <input 
                          type="number" 
                          name="room_single_count" 
                          min="0" 
                          placeholder="0"
                          className="w-16 px-2 py-1 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white text-center font-semibold" 
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mål og vægt på kørestol */}
            {(hasManualChair || hasElectricChair) && (
              <div className="border-t border-[#99A18C]/10 pt-6 space-y-6 transition-all duration-300">
                <div>
                  <h4 className="font-bold text-gray-800 text-base">{texts.chairDimensions}</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    {texts.chairDimensionsDesc}
                  </p>
                </div>
                
                <div className={`grid gap-6 ${hasManualChair && hasElectricChair ? 'md:grid-cols-2' : 'max-w-md'}`}>
                  {/* Manuel stol mål */}
                  {hasManualChair && (
                    <div className="bg-stone-50/60 p-5 rounded-2xl border border-gray-100 space-y-4 transition-all">
                      <p className="font-semibold text-[#99A18C] text-sm border-b border-[#99A18C]/10 pb-1.5">{texts.manualChair}</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{texts.height}</label>
                          <input type="text" name="chair_manual_height" placeholder="f.eks. 90" className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{texts.width}</label>
                          <input type="text" name="chair_manual_width" placeholder="f.eks. 65" className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{texts.depth}</label>
                          <input type="text" name="chair_manual_depth" placeholder="f.eks. 100" className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{texts.weight}</label>
                          <input type="text" name="chair_manual_weight" placeholder="f.eks. 15" className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* El-stol mål */}
                  {hasElectricChair && (
                    <div className="bg-stone-50/60 p-5 rounded-2xl border border-gray-100 space-y-4 transition-all">
                      <p className="font-semibold text-[#99A18C] text-sm border-b border-[#99A18C]/10 pb-1.5">{texts.electricChair}</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{texts.height}</label>
                          <input type="text" name="chair_electric_height" placeholder="f.eks. 110" className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{texts.width}</label>
                          <input type="text" name="chair_electric_width" placeholder="f.eks. 70" className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{texts.depth}</label>
                          <input type="text" name="chair_electric_depth" placeholder="f.eks. 120" className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{texts.weight}</label>
                          <input type="text" name="chair_electric_weight" placeholder="f.eks. 95" className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none bg-white transition-all" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Detaljer for hjælpemidler, ekstra pleje og særlige behov */}
            <div className="border-t border-[#99A18C]/10 pt-6">
              <div className="space-y-2">
                <label className="block font-medium text-gray-800">{texts.specialNeeds}</label>
                <textarea name="special_needs" rows="3" placeholder={texts.specialNeedsPlaceholder} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all"></textarea>
                <div className="text-xs text-slate-500 leading-relaxed bg-stone-50 p-3.5 rounded-xl border border-stone-100 mt-2">
                  <span className="font-semibold text-[#99A18C]">{texts.importantInfo}</span> {texts.importantInfoDesc}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{texts.additionalComments}</label>
              <textarea name="comments" rows="4" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#99A18C]/20 outline-none transition-all"></textarea>
            </div>
          </div>

          <div className="bg-[#99A18C]/10 p-6 rounded-2xl mb-4 text-sm text-[#99A18C] italic leading-relaxed">
            {texts.disclaimer}
          </div>

          <div className="bg-[#99A18C]/10 p-6 rounded-2xl mb-8">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input required type="checkbox" name="consent" className="mt-1 w-5 h-5 text-[#99A18C] rounded focus:ring-[#99A18C]/20" />
              <span className="text-sm text-[#99A18C] leading-relaxed font-medium">
                {texts.consentText}
              </span>
            </label>
          </div>

          <button 
            disabled={loading}
            type="submit" 
            className="w-full bg-[#99A18C] text-white py-5 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 hover:bg-[#8a917e] transition-all shadow-xl hover:shadow-[#99A18C]/20 disabled:opacity-50"
          >
            {loading ? texts.sendingBtn : (
              <>
                {texts.submitBtn}
                <Send className="w-5 h-5" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RequestQuotePage;
